import {
    Button,
    Flex,
    Hint,
    InputErrorMessage,
    Label,
    Link,
    ModalActions,
    Radio,
    type SingleValue,
    Tooltip,
} from '@lokalise/louis';
import { Form, useFormikContext } from 'formik';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import type { AppListItem } from '@app/api/apps';
import type { Language } from '@app/api/languages';
import MainSpinner from '@app/components/MainSpinner';
import CheckboxField from '@app/components/form/CheckboxField';
import InputField from '@app/components/form/InputField';
import RadioGroupField from '@app/components/form/RadioGroupField';
import SelectField from '@app/components/form/SelectField';
import useCmdEnterKeyPress from '@app/hooks/useCmdEnterKeyPress';
import useEffectOnce from '@app/hooks/useEffectOnce';
import track, { AnalyticsEventName } from '@app/utils/analytics';

import type { SubmitValues } from '.';
import { ProjectType } from '../../../../types/project';
import removeQueryParamFromUrl from '../../helpers';
import usePreSelectedApp from '../../hooks/usePreSelectedApp';
import useQueryParam from '../../hooks/useQueryParam';
import IntegrationAppOptionLabel from '../IntegrationAppOptionLabel';
import { Container, RadioWrapper, TabForm } from '../NewProject/styles';
import { FileInput, FileNameSpan, SegmentationCustomContainer, SegmentationOptions } from './styles';

export type CreateFormProps = {
    isSegmentationEnabled: boolean;
    languagesLoading: boolean;
    languages: Language[];
    active: boolean;
    loading: boolean;
    minified?: boolean;
    onClose: () => void;
    selectedProjectType?: string | null;
    goBackToSelectionScreen?: () => void;
    contentIntegrationAppsLoading?: boolean;
    contentIntegrationApps?: AppListItem[];
};

const CreateForm = ({
    isSegmentationEnabled,
    languages,
    languagesLoading,
    active,
    minified = false,
    loading,
    onClose,
    selectedProjectType,
    goBackToSelectionScreen,
    contentIntegrationAppsLoading,
    contentIntegrationApps,
}: CreateFormProps) => {
    const { submitForm, values, setFieldValue, errors } = useFormikContext<SubmitValues>();

    const slugParam = useQueryParam('appSlug');
    const navigate = useNavigate();

    const { preSelectedAppLabel } = usePreSelectedApp(contentIntegrationApps || []);

    const languageOptions = useMemo(
        () => languages.map(({ langIso, name, id }) => ({ value: id, label: `${name} (${langIso})` })),
        [languages],
    );

    const contentIntegrationAppsOptions = useMemo(
        () =>
            contentIntegrationApps?.map((app) => ({
                value: app.slug,
                label: app.title,
                disabled: !app.isAvailable,
            })),
        [contentIntegrationApps],
    );

    const onSubmitKeyDownHandler = () => {
        track(AnalyticsEventName.PROJECT_CREATE_SUBMIT_CLICKED, {
            action: 'keypress',
        });

        return submitForm();
    };

    const submitShortcut = useCmdEnterKeyPress(!active && !loading, onSubmitKeyDownHandler);

    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffectOnce(() => {
        if (selectedProjectType !== values.projectType) {
            setFieldValue('projectType', selectedProjectType);
        }
    });

    const onIntegrationAppChange = (selectedApp: SingleValue<{ label: string; value: string }>) => {
        if (selectedApp && slugParam) {
            navigate(`/projects?appSlug=${selectedApp.value}#modal:new-project`);
            setFieldValue('contentIntegration', selectedApp);
        }
    };

    useEffect(() => {
        if (slugParam && preSelectedAppLabel) {
            setFieldValue('contentIntegration', { label: preSelectedAppLabel, value: slugParam });
        }
    }, [preSelectedAppLabel, setFieldValue, slugParam]);

    return (
        <Form>
            <Container>
                {loading && <MainSpinner position="absolute" />}
                <TabForm $minified={minified}>
                    <Label
                        text={
                            <>
                                Project name{' '}
                                <Hint ariaLabel="Project name hint">
                                    Lokalise allows you to keep different platform strings in the same project – no more
                                    duplicate translation work. Learn how to{' '}
                                    <Link
                                        href="https://docs.lokalise.com/en/articles/1400533-duplicate-translations#merging-keys"
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label="Merge iOS and Android key"
                                    >
                                        merge iOS and Android keys
                                    </Link>{' '}
                                    after the upload is finished.
                                </Hint>
                            </>
                        }
                    >
                        <InputField autoFocus name="name" placeholder="MyApp (iOS + Android + Web)" />
                    </Label>
                    <Label id="BaseLanguage" text="Base language">
                        <SelectField
                            aria-labelledby="BaseLanguage"
                            name="sourceLanguage"
                            isLoading={languagesLoading}
                            options={languageOptions}
                        />
                    </Label>
                    <Label
                        id="TargetLanguage"
                        text={
                            <>
                                Target languages{' '}
                                <Hint>
                                    Select the languages you want to translate into. You&apos;ll be able to edit these
                                    later.
                                </Hint>
                            </>
                        }
                    >
                        <SelectField
                            aria-labelledby="TargetLanguage"
                            name="targetLanguages"
                            isLoading={languagesLoading}
                            options={languageOptions}
                            isMulti
                            placeholder="Select target language..."
                        />
                    </Label>
                    {isSegmentationEnabled && selectedProjectType !== ProjectType.contentIntegration ? (
                        <Label
                            text={
                                <>
                                    Segmentation{' '}
                                    <Hint ariaLabel="Segmentation hint">
                                        Segmentation makes it easier to work with longer texts by splitting them into
                                        individual segments. Segments will be merged upon export - they do not affect
                                        the structure of your files.
                                    </Hint>
                                </>
                            }
                        >
                            <CheckboxField name="isSegmentationEnabled">Split text into segments</CheckboxField>
                            {values.isSegmentationEnabled ? (
                                <SegmentationOptions>
                                    <RadioGroupField name="segmentationRulesType">
                                        <RadioWrapper>
                                            <Radio value="default">Default rules</Radio>
                                            <Radio value="custom">Custom rules</Radio>

                                            {values.segmentationRulesType === 'custom' && (
                                                <SegmentationCustomContainer>
                                                    <Flex justify="space-between" align="center">
                                                        <Flex gap={2}>
                                                            <Button
                                                                size="xs"
                                                                variant="secondary"
                                                                onClick={() => inputRef.current?.click()}
                                                            >
                                                                Upload SRX file
                                                            </Button>
                                                            <FileInput
                                                                aria-labelledby="segmentationRulesFile"
                                                                ref={inputRef}
                                                                name="segmentationRulesFile"
                                                                type="file"
                                                                accept=".srx"
                                                                aria-label="Upload SRX file"
                                                                onChange={(e) => {
                                                                    setFieldValue(
                                                                        'segmentationRulesFile',
                                                                        e.target.files && e.target.files[0],
                                                                    );
                                                                }}
                                                            />
                                                            <FileNameSpan>
                                                                {values.segmentationRulesFile?.name ||
                                                                    'No file selected'}
                                                            </FileNameSpan>
                                                        </Flex>
                                                        <Flex gap={1}>
                                                            <Link
                                                                style={{ fontSize: 11, textAlign: 'right' }}
                                                                aria-label="Download default-rules"
                                                                href="/segmentation/rules"
                                                                rel="noreferrer"
                                                            >
                                                                Download default-rules template
                                                            </Link>
                                                        </Flex>
                                                    </Flex>
                                                </SegmentationCustomContainer>
                                            )}
                                            {values.segmentationRulesType === 'custom' &&
                                                errors.segmentationRulesFile && (
                                                    <InputErrorMessage id="segmentationRulesFile">
                                                        {errors.segmentationRulesFile}
                                                    </InputErrorMessage>
                                                )}
                                        </RadioWrapper>
                                    </RadioGroupField>
                                </SegmentationOptions>
                            ) : null}
                        </Label>
                    ) : null}
                    {selectedProjectType === ProjectType.contentIntegration && (
                        <Label
                            text={
                                <>
                                    Select content integration{' '}
                                    <Hint ariaLabel="Content integration hint">
                                        Please note that you can only connect one Marketing and Support integration per
                                        project. You won&apos;t be able to switch the selected integration for this
                                        project later.
                                    </Hint>
                                </>
                            }
                        >
                            <SelectField
                                aria-labelledby="ContentIntegrationApp"
                                name="contentIntegration"
                                placeholder="Select integration..."
                                isLoading={contentIntegrationAppsLoading}
                                options={contentIntegrationAppsOptions}
                                isOptionDisabled={(option) => option.disabled}
                                formatOptionLabel={({ label, disabled }) => (
                                    <IntegrationAppOptionLabel label={label} disabled={disabled} />
                                )}
                                value={values.contentIntegration.value ? values.contentIntegration : null}
                                getOptionValue={(option) => option.value}
                                {...(slugParam ? { onChange: onIntegrationAppChange } : {})}
                            />
                        </Label>
                    )}
                </TabForm>
                <ModalActions>
                    {goBackToSelectionScreen ? (
                        <Button
                            variant="secondary"
                            onClick={() => {
                                if (slugParam) {
                                    removeQueryParamFromUrl('appSlug');
                                }
                                goBackToSelectionScreen();
                            }}
                        >
                            Back
                        </Button>
                    ) : (
                        <Button
                            variant="secondary"
                            onClick={() => {
                                onClose();
                            }}
                        >
                            Cancel
                        </Button>
                    )}
                    <Tooltip tooltip={submitShortcut}>
                        <Button
                            variant="primary"
                            type="submit"
                            onClick={() => {
                                track(AnalyticsEventName.PROJECT_CREATE_SUBMIT_CLICKED, {
                                    action: 'click',
                                });
                            }}
                        >
                            Create project
                        </Button>
                    </Tooltip>
                </ModalActions>
            </Container>
        </Form>
    );
};

export default CreateForm;
